import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { safeArray } from '../../utils/money';
import TreasuryService, {
  Account,
  Transfer,
  Settlement,
  ConversionRequest,
} from '../../services/treasury';

interface TreasuryState {
  accounts: {
    data: Account[];
    loading: boolean;
    error: string | null;
  };
  currentAccount: Account | null;
  transfers: {
    data: Transfer[];
    meta: any;
    loading: boolean;
  };
  currentTransfer: Transfer | null;
  settlements: {
    data: Settlement[];
    meta: any;
    loading: boolean;
  };
  currentSettlement: Settlement | null;
  transactions: {
    data: any[];
    meta: any;
    loading: boolean;
  };
  exchangeRate: {
    from_currency: string;
    to_currency: string;
    rate: number;
    updated_at: string;
  } | null;
  creating: boolean;
  converting: boolean;
  error: string | null;
}

const initialState: TreasuryState = {
  accounts: {
    data: [],
    loading: false,
    error: null,
  },
  currentAccount: null,
  transfers: {
    data: [],
    meta: null,
    loading: false,
  },
  currentTransfer: null,
  settlements: {
    data: [],
    meta: null,
    loading: false,
  },
  currentSettlement: null,
  transactions: {
    data: [],
    meta: null,
    loading: false,
  },
  exchangeRate: null,
  creating: false,
  converting: false,
  error: null,
};

// Accounts
export const fetchAccounts = createAsyncThunk(
  'treasury/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      return await TreasuryService.getAccounts();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch accounts');
    }
  }
);

export const fetchAccountDetail = createAsyncThunk(
  'treasury/fetchAccountDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await TreasuryService.getAccount(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch account');
    }
  }
);

export const createAccount = createAsyncThunk(
  'treasury/createAccount',
  async (data: { currency: string; type: string }, { rejectWithValue }) => {
    try {
      return await TreasuryService.createAccount(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create account');
    }
  }
);

export const updateAccount = createAsyncThunk(
  'treasury/updateAccount',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      return await TreasuryService.updateAccount(id, data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update account');
    }
  }
);

// Transfers
export const fetchTransfers = createAsyncThunk(
  'treasury/fetchTransfers',
  async (
    params: { page?: number; limit?: number; filters?: any },
    { rejectWithValue }
  ) => {
    try {
      return await TreasuryService.listTransfers(
        params.page,
        params.limit,
        params.filters
      );
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transfers');
    }
  }
);

export const fetchTransferDetail = createAsyncThunk(
  'treasury/fetchTransferDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await TreasuryService.getTransfer(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transfer');
    }
  }
);

export const createTransfer = createAsyncThunk(
  'treasury/createTransfer',
  async (
    data: {
      from_account_id: string;
      to_account_id: string;
      amount: number;
      currency: string;
      description?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      return await TreasuryService.createTransfer(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create transfer');
    }
  }
);

export const cancelTransfer = createAsyncThunk(
  'treasury/cancelTransfer',
  async (id: string, { rejectWithValue }) => {
    try {
      await TreasuryService.cancelTransfer(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel transfer');
    }
  }
);

// Settlements
export const fetchSettlements = createAsyncThunk(
  'treasury/fetchSettlements',
  async (
    params: { page?: number; limit?: number; filters?: any },
    { rejectWithValue }
  ) => {
    try {
      return await TreasuryService.listSettlements(
        params.page,
        params.limit,
        params.filters
      );
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch settlements');
    }
  }
);

export const fetchSettlementDetail = createAsyncThunk(
  'treasury/fetchSettlementDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await TreasuryService.getSettlement(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch settlement');
    }
  }
);

export const createSettlement = createAsyncThunk(
  'treasury/createSettlement',
  async (
    data: {
      amount: number;
      currency: string;
      destination_type: string;
      destination_id?: string;
      destination_details: any;
    },
    { rejectWithValue }
  ) => {
    try {
      return await TreasuryService.createSettlement(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create settlement');
    }
  }
);

export const cancelSettlement = createAsyncThunk(
  'treasury/cancelSettlement',
  async (id: string, { rejectWithValue }) => {
    try {
      await TreasuryService.cancelSettlement(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel settlement');
    }
  }
);

// Currency Exchange
export const convertCurrency = createAsyncThunk(
  'treasury/convertCurrency',
  async (data: ConversionRequest, { rejectWithValue }) => {
    try {
      return await TreasuryService.convertCurrency(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to convert currency');
    }
  }
);

export const fetchExchangeRate = createAsyncThunk(
  'treasury/fetchExchangeRate',
  async ({ from, to }: { from: string; to: string }, { rejectWithValue }) => {
    try {
      return await TreasuryService.getExchangeRate(from, to);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch exchange rate');
    }
  }
);

// Transactions
export const fetchTransactions = createAsyncThunk(
  'treasury/fetchTransactions',
  async (
    params: { page?: number; limit?: number; filters?: any },
    { rejectWithValue }
  ) => {
    try {
      return await TreasuryService.listTransactions(
        params.page,
        params.limit,
        params.filters
      );
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions');
    }
  }
);

export const fetchTransactionDetail = createAsyncThunk(
  'treasury/fetchTransactionDetail',
  async (id: string, { rejectWithValue }) => {
    try {
      return await TreasuryService.getTransaction(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transaction');
    }
  }
);

const treasurySlice = createSlice({
  name: 'treasury',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.accounts.error = null;
    },
    clearCurrentAccount: (state) => {
      state.currentAccount = null;
    },
    clearCurrentTransfer: (state) => {
      state.currentTransfer = null;
    },
    clearCurrentSettlement: (state) => {
      state.currentSettlement = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Accounts
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.accounts.loading = true;
        state.accounts.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action: PayloadAction<Account[]>) => {
        state.accounts.loading = false;
        state.accounts.data = action.payload;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.accounts.loading = false;
        state.accounts.error = action.payload as string;
      });

    // Fetch Account Detail
    builder
      .addCase(fetchAccountDetail.pending, (state) => {
        state.accounts.loading = true;
      })
      .addCase(fetchAccountDetail.fulfilled, (state, action: PayloadAction<Account>) => {
        state.accounts.loading = false;
        state.currentAccount = action.payload;
      })
      .addCase(fetchAccountDetail.rejected, (state, action) => {
        state.accounts.loading = false;
        state.error = action.payload as string;
      });

    // Create Account
    builder
      .addCase(createAccount.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state, action: PayloadAction<Account>) => {
        state.creating = false;
        state.accounts.data.push(action.payload);
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Update Account
    builder
      .addCase(updateAccount.pending, (state) => {
        state.creating = true;
      })
      .addCase(updateAccount.fulfilled, (state, action: PayloadAction<Account>) => {
        state.creating = false;
        const index = state.accounts.data.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.accounts.data[index] = action.payload;
        }
        if (state.currentAccount?.id === action.payload.id) {
          state.currentAccount = action.payload;
        }
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Fetch Transfers
    builder
      .addCase(fetchTransfers.pending, (state) => {
        state.transfers.loading = true;
      })
      .addCase(
        fetchTransfers.fulfilled,
        (state, action: PayloadAction<{ data: Transfer[]; meta: any }>) => {
          state.transfers.loading = false;
          state.transfers.data = safeArray<Transfer>(action.payload.data);
          state.transfers.meta = action.payload.meta;
        }
      )
      .addCase(fetchTransfers.rejected, (state, action) => {
        state.transfers.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Transfer Detail
    builder
      .addCase(fetchTransferDetail.pending, (state) => {
        state.transfers.loading = true;
      })
      .addCase(fetchTransferDetail.fulfilled, (state, action: PayloadAction<Transfer>) => {
        state.transfers.loading = false;
        state.currentTransfer = action.payload;
      })
      .addCase(fetchTransferDetail.rejected, (state, action) => {
        state.transfers.loading = false;
        state.error = action.payload as string;
      });

    // Create Transfer
    builder
      .addCase(createTransfer.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createTransfer.fulfilled, (state, action: PayloadAction<Transfer>) => {
        state.creating = false;
        state.transfers.data.unshift(action.payload);
      })
      .addCase(createTransfer.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Cancel Transfer
    builder
      .addCase(cancelTransfer.pending, (state) => {
        state.creating = true;
      })
      .addCase(cancelTransfer.fulfilled, (state, action: PayloadAction<string>) => {
        state.creating = false;
        const transfer = state.transfers.data.find((t) => t.id === action.payload);
        if (transfer) {
          transfer.status = 'cancelled';
        }
      })
      .addCase(cancelTransfer.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Fetch Settlements
    builder
      .addCase(fetchSettlements.pending, (state) => {
        state.settlements.loading = true;
      })
      .addCase(
        fetchSettlements.fulfilled,
        (state, action: PayloadAction<{ data: Settlement[]; meta: any }>) => {
          state.settlements.loading = false;
          state.settlements.data = safeArray<Settlement>(action.payload.data);
          state.settlements.meta = action.payload.meta;
        }
      )
      .addCase(fetchSettlements.rejected, (state, action) => {
        state.settlements.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Settlement Detail
    builder
      .addCase(fetchSettlementDetail.pending, (state) => {
        state.settlements.loading = true;
      })
      .addCase(
        fetchSettlementDetail.fulfilled,
        (state, action: PayloadAction<Settlement>) => {
          state.settlements.loading = false;
          state.currentSettlement = action.payload;
        }
      )
      .addCase(fetchSettlementDetail.rejected, (state, action) => {
        state.settlements.loading = false;
        state.error = action.payload as string;
      });

    // Create Settlement
    builder
      .addCase(createSettlement.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createSettlement.fulfilled, (state, action: PayloadAction<Settlement>) => {
        state.creating = false;
        state.settlements.data.unshift(action.payload);
      })
      .addCase(createSettlement.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Cancel Settlement
    builder
      .addCase(cancelSettlement.pending, (state) => {
        state.creating = true;
      })
      .addCase(cancelSettlement.fulfilled, (state, action: PayloadAction<string>) => {
        state.creating = false;
        const settlement = state.settlements.data.find((s) => s.id === action.payload);
        if (settlement) {
          settlement.status = 'cancelled';
        }
      })
      .addCase(cancelSettlement.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });

    // Convert Currency
    builder
      .addCase(convertCurrency.pending, (state) => {
        state.converting = true;
        state.error = null;
      })
      .addCase(convertCurrency.fulfilled, (state) => {
        state.converting = false;
      })
      .addCase(convertCurrency.rejected, (state, action) => {
        state.converting = false;
        state.error = action.payload as string;
      });

    // Fetch Exchange Rate
    builder
      .addCase(fetchExchangeRate.pending, (state) => {
        state.converting = true;
      })
      .addCase(
        fetchExchangeRate.fulfilled,
        (
          state,
          action: PayloadAction<{
            from_currency: string;
            to_currency: string;
            rate: number;
            updated_at: string;
          }>
        ) => {
          state.converting = false;
          state.exchangeRate = action.payload;
        }
      )
      .addCase(fetchExchangeRate.rejected, (state, action) => {
        state.converting = false;
        state.error = action.payload as string;
      });

    // Fetch Transactions
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.transactions.loading = true;
      })
      .addCase(
        fetchTransactions.fulfilled,
        (state, action: PayloadAction<{ data: any[]; meta: any }>) => {
          state.transactions.loading = false;
          state.transactions.data = safeArray(action.payload.data);
          state.transactions.meta = action.payload.meta;
        }
      )
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.transactions.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Transaction Detail
    builder
      .addCase(fetchTransactionDetail.pending, (state) => {
        state.transactions.loading = true;
      })
      .addCase(fetchTransactionDetail.fulfilled, (state) => {
        state.transactions.loading = false;
      })
      .addCase(fetchTransactionDetail.rejected, (state, action) => {
        state.transactions.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentAccount,
  clearCurrentTransfer,
  clearCurrentSettlement,
} = treasurySlice.actions;

export default treasurySlice.reducer;
